import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type {
  DispatchAssignmentStatus,
  DispatchAssignResponse,
  DispatchCategoryCreateResponse,
  DispatchCategoryDeleteResponse,
  DispatchDeleteResponse,
  DispatchRescheduleResponse,
  DispatchStatusResponse,
} from './contractsMirror.js';

/**
 * The dispatch mutations (the board's write paths). All ride `apiFetch` (the only fetch path —
 * `credentials:'include'` + `X-Tenant-Subdomain` + the double-submit CSRF on the non-safe method)
 * and carry NO tenantId — the server resolves the tenant from the principal AND ownership-checks
 * the employee/job/category are tenant-owned (SEC-11 / T-22-tenant). On success each invalidates
 * the `['dispatch']` week query so the board re-syncs to the authoritative server state.
 *
 *   • useAssignMutation       — POST   /api/v1/dispatch/assign          (drag-to-day / Schedule… dialog)
 *   • useRescheduleMutation   — PATCH  /api/v1/dispatch/:id             (drag a card to a new day)
 *   • useEditMutation         — PATCH  /api/v1/dispatch/:id             (full-field edit dialog)
 *   • useUnassignMutation     — DELETE /api/v1/dispatch/:id             (remove from the board)
 *   • useStatusMutation       — PATCH  /api/v1/dispatch/:id/status
 *   • useCreateCategoryMutation — POST   /api/v1/dispatch/categories
 *   • useDeleteCategoryMutation — DELETE /api/v1/dispatch/categories/:id
 */

export interface AssignInput {
  employeeId: number;
  scheduledDate: string;
  startTime: string;
  endTime?: string | null;
  jobId?: number | null;
  categoryId?: number | null;
  notes?: string | null;
  customerName?: string | null;
  customerPhone?: string | null;
  customerAddress?: string | null;
}

export function useAssignMutation() {
  const queryClient = useQueryClient();

  return useMutation<DispatchAssignResponse, Error, AssignInput>({
    mutationFn: (input) =>
      apiFetchJson<DispatchAssignResponse>('/api/v1/dispatch/assign', {
        method: 'POST',
        // NO tenantId — the server resolves the tenant from the principal (SEC-11).
        body: JSON.stringify({
          employee_id: input.employeeId,
          scheduled_date: input.scheduledDate,
          start_time: input.startTime,
          end_time: input.endTime ?? undefined,
          job_id: input.jobId ?? undefined,
          category_id: input.categoryId ?? undefined,
          notes: input.notes ?? undefined,
          customer_name: input.customerName ?? undefined,
          customer_phone: input.customerPhone ?? undefined,
          customer_address: input.customerAddress ?? undefined,
        }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dispatch'] });
    },
  });
}

export interface RescheduleInput {
  id: number;
  scheduledDate?: string;
  startTime?: string;
  endTime?: string | null;
  employeeId?: number;
}

export function useRescheduleMutation() {
  const queryClient = useQueryClient();

  return useMutation<DispatchRescheduleResponse, Error, RescheduleInput>({
    mutationFn: ({ id, scheduledDate, startTime, endTime, employeeId }) =>
      apiFetchJson<DispatchRescheduleResponse>(`/api/v1/dispatch/${id}`, {
        method: 'PATCH',
        // NO tenantId — the server scopes the assignment to the principal tenant (SEC-11).
        body: JSON.stringify({
          scheduled_date: scheduledDate,
          start_time: startTime,
          end_time: endTime ?? undefined,
          employee_id: employeeId,
        }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dispatch'] });
    },
  });
}

export function useStatusMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    DispatchStatusResponse,
    Error,
    { id: number; status: DispatchAssignmentStatus }
  >({
    mutationFn: ({ id, status }) =>
      apiFetchJson<DispatchStatusResponse>(`/api/v1/dispatch/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dispatch'] });
    },
  });
}

/** The full-field edit — every field is optional; the server keeps unsupplied fields (SEC-11). */
export interface EditInput {
  id: number;
  employeeId?: number;
  scheduledDate?: string;
  startTime?: string;
  endTime?: string | null;
  jobId?: number | null;
  categoryId?: number | null;
  notes?: string | null;
  customerName?: string | null;
  customerPhone?: string | null;
  customerAddress?: string | null;
}

export function useEditMutation() {
  const queryClient = useQueryClient();

  return useMutation<DispatchRescheduleResponse, Error, EditInput>({
    mutationFn: ({ id, ...input }) =>
      apiFetchJson<DispatchRescheduleResponse>(`/api/v1/dispatch/${id}`, {
        method: 'PATCH',
        // NO tenantId — the server scopes the assignment to the principal tenant and ownership-checks
        // any supplied job/category (SEC-11 / T-22-tenant). Only supplied keys are sent; unsent keys
        // keep their existing server value.
        body: JSON.stringify({
          employee_id: input.employeeId,
          scheduled_date: input.scheduledDate,
          start_time: input.startTime,
          end_time: input.endTime,
          job_id: input.jobId,
          category_id: input.categoryId,
          notes: input.notes,
          customer_name: input.customerName,
          customer_phone: input.customerPhone,
          customer_address: input.customerAddress,
        }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dispatch'] });
    },
  });
}

export function useUnassignMutation() {
  const queryClient = useQueryClient();

  return useMutation<DispatchDeleteResponse, Error, { id: number }>({
    mutationFn: ({ id }) =>
      apiFetchJson<DispatchDeleteResponse>(`/api/v1/dispatch/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dispatch'] });
    },
  });
}

export function useCreateCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation<DispatchCategoryCreateResponse, Error, { name: string; color: string }>({
    mutationFn: ({ name, color }) =>
      apiFetchJson<DispatchCategoryCreateResponse>('/api/v1/dispatch/categories', {
        method: 'POST',
        body: JSON.stringify({ name, color }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dispatch'] });
    },
  });
}

export function useDeleteCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation<DispatchCategoryDeleteResponse, Error, { id: number }>({
    mutationFn: ({ id }) =>
      apiFetchJson<DispatchCategoryDeleteResponse>(`/api/v1/dispatch/categories/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dispatch'] });
    },
  });
}
